import { useEffect, useMemo, useState } from 'react';
import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { PageHeader } from '../../components/PageHeader';
import { StatusBanner } from '../../components/StatusBanner';
import { Spinner } from '../../components/Spinner';
import { TRACKED_REG } from '../../lib/flightTrack';
import { approvalState } from '../../lib/flightApproval';
import type { BookingDoc, FlightApprovalDoc } from '../../lib/types';

/**
 * Per-flight approval of student pilot bookings — Old Republic's Student Pilot
 * Restriction (Wings, in writing 10/15/2026): the student flies only under a CFI
 * named on PB222 "who shall have specifically approved each flight by the Student
 * prior to take-off". Lease §4.3(a)/(e) points here as the record of that approval.
 *
 * CFIs only. The owner can see and revoke approvals from the admin side but can't
 * give one — he isn't a CFI, and this is the carrier's rule, not AJM's.
 */
export function StudentFlightApproval() {
  const { fbUser, profile, demoMode } = useAuth();
  const isCfi = profile?.billingProfile === 'cfi' && profile?.status === 'active';
  const cfiName = `${profile?.personal?.firstName ?? ''} ${profile?.personal?.lastName ?? ''}`.trim();

  const [students, setStudents] = useState<Record<string, string> | null>(null);
  const [bookings, setBookings] = useState<BookingDoc[] | null>(null);
  const [approvals, setApprovals] = useState<Record<string, FlightApprovalDoc>>({});
  const [error, setError] = useState('');

  useEffect(() => {
    if (!db || !isCfi || demoMode) return;
    getDocs(query(collection(db, 'pilotDirectory'), where('studentPilot', '==', true)))
      .then((s) => {
        const m: Record<string, string> = {};
        s.docs.forEach((d) => {
          const x = d.data() as { firstName?: string; lastName?: string };
          m[d.id] = `${x.firstName ?? ''} ${x.lastName ?? ''}`.trim() || 'Student pilot';
        });
        setStudents(m);
      })
      .catch((e) => {
        console.error('pilotDirectory', e);
        setError("Couldn't load the student list. Ask Casey to check your CFI access.");
        setStudents({});
      });
    const since = Timestamp.fromMillis(Date.now() - 6 * 3600 * 1000);
    const unsubB = onSnapshot(
      query(collection(db, 'bookings'), where('endDateTime', '>=', since)),
      (s) => setBookings(s.docs.map((d) => ({ ...(d.data() as BookingDoc), bookingId: d.id }))),
      (e) => {
        console.error('bookings', e);
        setError("Couldn't load bookings.");
        setBookings([]);
      }
    );
    const unsubA = onSnapshot(collection(db, 'flightApprovals'), (s) => {
      const m: Record<string, FlightApprovalDoc> = {};
      s.docs.forEach((d) => (m[d.id] = d.data() as FlightApprovalDoc));
      setApprovals(m);
    });
    return () => {
      unsubB();
      unsubA();
    };
  }, [isCfi, demoMode]);

  const studentBookings = useMemo(() => {
    if (!students || !bookings) return null;
    return bookings
      .filter((b) => students[b.lesseeUid] && ['auto_confirmed', 'approved', 'pending_approval'].includes(b.status))
      .sort((a, b) => a.startDateTime.toMillis() - b.startDateTime.toMillis());
  }, [students, bookings]);

  if (!isCfi) {
    return (
      <StatusBanner tone="warning" title="Instructors only">
        Student flights are approved by flight instructors named on AJM&rsquo;s insurance policy (PB222).
      </StatusBanner>
    );
  }

  return (
    <>
      <PageHeader
        title="Approve student flights"
        subtitle={`Every student flight in ${TRACKED_REG}, dual or solo, needs your approval here before takeoff.`}
      />
      <div className="card mb-4 p-4 text-sm text-ink-muted">
        Old Republic requires student pilots to fly under the supervision of a CFI named on PB222 who has
        specifically approved each flight before takeoff. Your approval locks to the booked times. If the
        student moves the booking, it needs approving again.
      </div>
      {error && (
        <StatusBanner tone="danger" title="Something went wrong">
          {error}
        </StatusBanner>
      )}
      {demoMode ? (
        <div className="card p-8 text-center text-ink-muted">Student bookings show up here for named instructors.</div>
      ) : !studentBookings ? (
        <Spinner label="Loading student bookings" />
      ) : studentBookings.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">No upcoming student bookings.</div>
      ) : (
        <div className="space-y-3">
          {studentBookings.map((b) => (
            <ApprovalCard
              key={b.bookingId}
              booking={b}
              studentName={students?.[b.lesseeUid] ?? 'Student pilot'}
              approval={approvals[b.bookingId]}
              cfiUid={fbUser?.uid ?? ''}
              cfiName={cfiName}
              defaultCert={profile?.pilot?.certificateNumber ?? ''}
            />
          ))}
        </div>
      )}
    </>
  );
}

function ApprovalCard({
  booking,
  studentName,
  approval,
  cfiUid,
  cfiName,
  defaultCert,
}: {
  booking: BookingDoc;
  studentName: string;
  approval?: FlightApprovalDoc;
  cfiUid: string;
  cfiName: string;
  defaultCert: string;
}) {
  const state = approvalState(booking, approval);
  const [kind, setKind] = useState<'dual' | 'solo'>('dual');
  const [soloOk, setSoloOk] = useState(false);
  const [certNumber, setCertNumber] = useState(defaultCert);
  const [notes, setNotes] = useState('');
  const [signature, setSignature] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const start = booking.startDateTime.toDate();
  const end = booking.endDateTime.toDate();
  const when = `${start.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} · ${start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} – ${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  const signatureMatches = Boolean(cfiName) && signature.trim().toLowerCase() === cfiName.toLowerCase();
  const canApprove = signatureMatches && Boolean(certNumber.trim()) && (kind === 'dual' || soloOk) && !busy;

  async function approve() {
    if (!db || !canApprove) return;
    setBusy(true);
    setErr('');
    try {
      const ref = doc(db, 'flightApprovals', booking.bookingId);
      // A stale approval (booking moved) has to go before the new one can be written.
      if (approval) await deleteDoc(ref);
      await setDoc(ref, {
        bookingId: booking.bookingId,
        aircraftId: TRACKED_REG,
        pilotUid: booking.lesseeUid,
        pilotName: studentName,
        cfiUid,
        cfiName,
        cfiCertNumber: certNumber.trim(),
        kind,
        approvedStart: booking.startDateTime,
        approvedEnd: booking.endDateTime,
        ...(kind === 'solo' ? { soloEndorsementsConfirmed: true } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        signatureName: signature.trim(),
        approvedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      setErr('Could not save the approval. Try again, or text Casey.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!db || !window.confirm('Revoke your approval for this flight?')) return;
    try {
      await deleteDoc(doc(db, 'flightApprovals', booking.bookingId));
    } catch (e) {
      console.error(e);
      setErr('Could not revoke. Try again.');
    }
  }

  return (
    <div className="card p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-base font-semibold text-navy-900">{studentName}</div>
          <div className="mt-0.5 text-sm text-ink-muted">{when}</div>
          {booking.expectedDestinations && <div className="mt-1 text-sm text-ink">{booking.expectedDestinations}</div>}
        </div>
        <span className={state === 'approved' ? 'badge-success' : 'badge-danger'}>
          {state === 'approved' ? `Approved · ${approval?.kind}` : state === 'stale' ? 'Moved · re-approve' : 'Not approved'}
        </span>
      </div>

      {state === 'approved' && approval ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3 text-sm text-ink-muted">
          <span>
            Approved by {approval.cfiName} ({approval.kind === 'solo' ? 'solo' : 'dual'})
            {approval.approvedAt ? ` on ${approval.approvedAt.toDate().toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
          </span>
          <button type="button" onClick={revoke} className="font-medium text-red-700 hover:underline">
            Revoke
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
          <div className="inline-flex rounded-xl bg-slate-100 p-1 text-sm">
            {(['dual', 'solo'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={`rounded-lg px-4 py-1.5 font-medium ${kind === k ? 'bg-white text-navy-900 shadow-sm' : 'text-ink-muted'}`}
              >
                {k === 'dual' ? 'Dual (I’m aboard)' : 'Solo'}
              </button>
            ))}
          </div>
          {kind === 'solo' && (
            <label className="flex items-start gap-2 text-sm text-ink">
              <input type="checkbox" className="mt-1" checked={soloOk} onChange={(e) => setSoloOk(e.target.checked)} />
              <span>
                The student holds current §61.87 solo endorsements for this make and model (including the 90-day
                endorsement), has a current medical, and I&rsquo;ve reviewed this flight and the weather.
              </span>
            </label>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="label">Your CFI certificate number</label>
              <input className="input" value={certNumber} onChange={(e) => setCertNumber(e.target.value)} />
            </div>
            <div>
              <label className="label">Type your full name to sign ({cfiName || 'your name'})</label>
              <input className="input" value={signature} onChange={(e) => setSignature(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label">Limits or notes (optional)</label>
            <input
              className="input"
              placeholder="e.g. pattern work only, winds under 12 kt"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          {err && <p className="text-sm text-red-700">{err}</p>}
          <button type="button" className="btn-primary text-sm" disabled={!canApprove} onClick={approve}>
            {busy ? 'Saving…' : state === 'stale' ? 'Re-approve this flight' : 'Approve this flight'}
          </button>
        </div>
      )}
    </div>
  );
}
